<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { PhPackage } from '@phosphor-icons/vue'
import { resolveMediaUrl } from '@/utils/media'

/**
 * A listing image in the one 16:9 frame every screen uses: cards, the gallery and
 * the upload previews, so a seller sees the same framing as a buyer.
 *
 * Images close to 16:9 fill the frame. Any other shape (a portrait phone photo,
 * a square logo) shows whole, on a blurred copy of itself, instead of losing its
 * edges. Overlays such as price badges go in the default slot.
 */
const props = defineProps<{
  src?: string | null
  alt: string
  /** Zoom in when a `group` ancestor is hovered. */
  zoom?: boolean
}>()

const FRAME_RATIO = 16 / 9
/** Share of an image the frame may cut off before it shows the whole image instead. */
const MAX_CROP = 0.12

const image = ref<HTMLImageElement>()
const fit = ref<'cover' | 'contain'>('cover')

function measure() {
  const img = image.value
  if (!img?.naturalWidth || !img.naturalHeight) return
  const ratio = img.naturalWidth / img.naturalHeight
  const cropped = 1 - Math.min(ratio, FRAME_RATIO) / Math.max(ratio, FRAME_RATIO)
  fit.value = cropped > MAX_CROP ? 'contain' : 'cover'
}

// A cached image can finish loading before the load listener runs.
onMounted(() => {
  if (image.value?.complete) measure()
})
watch(() => props.src, () => { fit.value = 'cover' })
</script>

<template>
  <div class="relative aspect-video overflow-hidden bg-surface-2">
    <template v-if="props.src">
      <img
        v-if="fit === 'contain'"
        :src="resolveMediaUrl(props.src)"
        alt=""
        aria-hidden="true"
        class="absolute inset-0 h-full w-full scale-110 object-cover opacity-60 blur-xl"
      />
      <img
        ref="image"
        :src="resolveMediaUrl(props.src)"
        :alt="props.alt"
        class="relative h-full w-full"
        :class="[
          fit === 'contain' ? 'object-contain' : 'object-cover',
          props.zoom && 'transition-transform duration-500 group-hover:scale-105',
        ]"
        loading="lazy"
        @load="measure"
      />
    </template>
    <PhPackage v-else class="absolute inset-0 m-auto h-10 w-10 text-ink-7" aria-hidden="true" />
    <slot />
  </div>
</template>
